import {
  AssistantMessage,
  Message,
  UserMessageContent,
} from "@redotech/redo-model/agents/llm-messages";

export const transformMessageContent = (message: Message): string => {
  if (message.role === "system") {
    return message.content;
  }
  if (message.role === "tool") {
    return "";
  }
  const content = message.content;
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content)) {
    return (content as UserMessageContent[])
      .map((part) => (part.type === "text" ? part.text : ""))
      .filter((text) => text.length > 0)
      .join(" ");
  }
  return String(content);
};

export const transformMessagesForBackend = (messages: Message[]) =>
  messages.map((message) => {
    if (message.role === "tool") {
      return message;
    }
    if (message.role === "assistant") {
      const assistantMsg = message as AssistantMessage;
      return {
        id: message.id,
        role: message.role,
        content: transformMessageContent(message),
        ...(assistantMsg.feedback !== undefined && {
          feedback: assistantMsg.feedback,
        }),
        ...(assistantMsg.comment !== undefined && {
          comment: assistantMsg.comment,
        }),
        ...(assistantMsg.usage !== undefined && { usage: assistantMsg.usage }),
      };
    }
    // Preserve content arrays for user messages (e.g. image parts)
    if (message.role === "user" && Array.isArray(message.content)) {
      return { id: message.id, role: message.role, content: message.content };
    }
    return {
      id: message.id,
      role: message.role,
      content: transformMessageContent(message),
    };
  });

export const buildIntercomTranscript = (messages: Message[]): string => {
  return messages
    .filter((m) => m.role !== "tool")
    .map((message) => {
      const content = transformMessageContent(message);
      // Skip messages with empty content (tool calls without responses)
      if (!content || content.trim() === "") {
        return null;
      }
      const role = message.role === "assistant" ? "AI" : "You";
      return `${role}:\n${content}`;
    })
    .filter((entry) => entry !== null)
    .join("\n\n");
};

/**
 * Extracts text content from a message, filtering out media data markers.
 * Handles both string content and array content formats.
 */
// Regex patterns for media markers - use non-greedy matching
// Patterns with 'g' flag for use with .replace()
const IMAGE_DATA_MARKER_REGEX = /\[\[\[\[image-data:.*?\]\]\]\]/g;
const VIDEO_DATA_MARKER_REGEX = /\[\[\[\[video-data:.*?\]\]\]\]/g;
const DOCUMENT_DATA_MARKER_REGEX = /\[\[\[\[document-data:.*?\]\]\]\]/g;

// Patterns without 'g' flag for use with .test() to avoid lastIndex state issues
const IMAGE_DATA_MARKER_TEST = /\[\[\[\[image-data:.*?\]\]\]\]/;
const VIDEO_DATA_MARKER_TEST = /\[\[\[\[video-data:.*?\]\]\]\]/;
const DOCUMENT_DATA_MARKER_TEST = /\[\[\[\[document-data:.*?\]\]\]\]/;

function filterMediaMarkersFromText(text: string): string {
  return text
    .replace(IMAGE_DATA_MARKER_REGEX, "")
    .replace(VIDEO_DATA_MARKER_REGEX, "")
    .replace(DOCUMENT_DATA_MARKER_REGEX, "")
    .trim();
}

export function extractTextFromContent(
  content: string | UserMessageContent[],
  options?: { filterMediaMarkers?: boolean },
): string {
  const { filterMediaMarkers = true } = options ?? {};

  if (typeof content === "string") {
    return filterMediaMarkers ? filterMediaMarkersFromText(content) : content;
  }

  if (!Array.isArray(content)) {
    return "";
  }

  const texts = content
    .filter((c): c is { type: "text"; text: string } => c.type === "text")
    .map((c) => c.text)
    .filter((text) => {
      if (!filterMediaMarkers) return true;
      // Filter out entries that are ONLY media markers
      const filtered = filterMediaMarkersFromText(text);
      return filtered.length > 0;
    })
    .map((text) =>
      filterMediaMarkers ? filterMediaMarkersFromText(text) : text,
    );

  return texts.join("\n");
}

/**
 * Extracts attachment URLs (images, videos) from message content.
 * Parses image content parts and video-data markers.
 */
export function extractAttachmentUrls(content: UserMessageContent[]): string[] {
  const urls: string[] = [];

  for (const part of content) {
    // Extract image URLs from image content parts
    if (
      part.type === "image" &&
      "image" in part &&
      typeof part.image === "string"
    ) {
      urls.push(part.image);
    }

    // Extract video URLs from video-data markers
    if (part.type === "text" && "text" in part) {
      const videoMatch = part.text.match(/\[\[\[\[video-data:(.*?)\]\]\]\]/);
      if (videoMatch?.[1]) {
        try {
          const videoData = JSON.parse(videoMatch[1]);
          if (videoData.url) {
            urls.push(videoData.url);
          }
        } catch {
          // Ignore parse errors
        }
      }
    }
  }

  return urls;
}

/**
 * Media marker types supported in chat messages.
 */
export type MediaMarkerType = "image" | "video" | "document";

/**
 * Parsed image data from an image-data marker.
 */
export interface ParsedImageData {
  type: "image";
  url: string;
  name?: string;
}

/**
 * Parsed video data from a video-data marker.
 */
export interface ParsedVideoData {
  type: "video";
  url: string;
  name: string;
}

/**
 * Parsed document data from a document-data marker.
 */
export interface ParsedDocumentData {
  type: "document";
  url?: string;
  name: string;
}

/**
 * Union type of all parsed media data.
 */
export type ParsedMediaData =
  | ParsedImageData
  | ParsedVideoData
  | ParsedDocumentData;

/**
 * Checks if text contains an image-data marker.
 */
export function isImageDataMarker(text: string): boolean {
  return IMAGE_DATA_MARKER_TEST.test(text);
}

/**
 * Checks if text contains a video-data marker.
 */
export function isVideoDataMarker(text: string): boolean {
  return VIDEO_DATA_MARKER_TEST.test(text);
}

/**
 * Checks if text contains a document-data marker.
 */
export function isDocumentDataMarker(text: string): boolean {
  return DOCUMENT_DATA_MARKER_TEST.test(text);
}

/**
 * Checks if text contains any media data marker.
 */
export function isAnyMediaMarker(text: string): boolean {
  return (
    isImageDataMarker(text) ||
    isVideoDataMarker(text) ||
    isDocumentDataMarker(text)
  );
}

/**
 * Parses an image-data marker and extracts the image data.
 * Returns null if parsing fails or no URL is found.
 */
export function parseImageDataMarker(text: string): ParsedImageData | null {
  const match = text.match(/\[\[\[\[image-data:(.*?)\]\]\]\]/);
  if (!match?.[1]) {
    return null;
  }

  try {
    const data = JSON.parse(match[1]) as { url?: string; name?: string };
    if (!data.url) {
      return null;
    }
    return { type: "image", url: data.url, name: data.name };
  } catch {
    return null;
  }
}

/**
 * Parses a video-data marker and extracts the video data.
 * Returns null if parsing fails or no URL is found.
 */
export function parseVideoDataMarker(text: string): ParsedVideoData | null {
  const match = text.match(/\[\[\[\[video-data:(.*?)\]\]\]\]/);
  if (!match?.[1]) {
    return null;
  }

  try {
    const data = JSON.parse(match[1]) as { url?: string; name?: string };
    if (!data.url) {
      return null;
    }
    return { type: "video", url: data.url, name: data.name ?? "video" };
  } catch {
    return null;
  }
}

/**
 * Parses a document-data marker and extracts the document data.
 * Returns null if parsing fails.
 */
export function parseDocumentDataMarker(
  text: string,
): ParsedDocumentData | null {
  const match = text.match(/\[\[\[\[document-data:(.*?)\]\]\]\]/);
  if (!match?.[1]) {
    return null;
  }

  try {
    const data = JSON.parse(match[1]) as { url?: string; name?: string };
    return { type: "document", url: data.url, name: data.name ?? "document" };
  } catch {
    return { type: "document", name: "document" };
  }
}

/**
 * Parses any media marker in the text and returns the parsed data.
 * Returns null if no media marker is found or parsing fails.
 */
export function parseMediaMarker(text: string): ParsedMediaData | null {
  if (isImageDataMarker(text)) {
    return parseImageDataMarker(text);
  }
  if (isVideoDataMarker(text)) {
    return parseVideoDataMarker(text);
  }
  if (isDocumentDataMarker(text)) {
    return parseDocumentDataMarker(text);
  }
  return null;
}

/**
 * Truncates a filename to a maximum length, preserving the extension if possible.
 */
export function truncateFilename(name: string, maxLength: number = 30): string {
  if (name.length <= maxLength) {
    return name;
  }
  return name.slice(0, maxLength) + "...";
}
